<template>
	<Modal
		width="800px"
	>
		<template #modal-title>
			Cookie - {{ selectedCookie.cookieName }}
			<v-spacer />
			<IconButton @click="closeModal()">
				mdi-close
			</IconButton>
		</template>v
		<template #modal-content>
			<div class="cassie-vertical-md">
				<TextArea
					v-model="description"
					:disabled="!userCanUseSiteScanner"
					rows="5"
					label="Cookie Description"
					:rules="{max: 1000}"
					tooltip-text="The Cookie Description. This can be updated to wording of your choice if required."
				/>
				<v-row dense>
					<v-col :cols="6">
						<PrimaryActionButton
							:disabled="!userCanUseSiteScanner"
							tooltip-text="Click to restore the default description provided by Syrenis where available. This will apply to all scans where this Cookie was captured. The 'Save Changes' button will commit any updates made."
							@click="restoreDefaultDescription()"
						>
							Restore Default Description
						</PrimaryActionButton>
					</v-col>
					<v-col :cols="6">
						<Dropdown
							v-model="cookieToManage.categoryId"
							:disabled="!userCanUseSiteScanner"
							:items="cookieCategoryItems"
							label="Category"
							tooltip-text="A known Cookie is assigned a Category. This Category can be updated if required."
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col
						class="d-flex align-center"
						:cols="2"
					>
						<span>
							Found in URLs
						</span>
					</v-col>
					<v-col
						class="d-flex align-center"
						:cols="3"
					>
						<v-progress-linear
							:value="getPercentage(cookieToManage.cookieCount)"
							height="20"
						>
							<strong>
								{{ cookieUrlCount(cookieToManage.cookieCount) }} / {{ totalUrls }}
							</strong>
						</v-progress-linear>
					</v-col>
					<v-col
						class="d-flex align-center"
						:cols="1"
					>
						<v-tooltip
							slot="append"
							top
						>
							<template #activator="{ on }">
								<v-icon
									light
									class="ml-4"
									v-on="on"
								>
									mdi-information
								</v-icon>
							</template>
							<span>
								{{ '"Found in URLs" advises how many pages this Cookie was discovered on during this scan.' }}
							</span>
						</v-tooltip>
					</v-col>
					<v-col
						:cols="4"
						class="pl-1"
					>
						<span class="text-subtitle-1">
							Type: {{ cookieToManage.type }}
						</span>
					</v-col>
				</v-row>
				<v-row>
					<v-col :cols="4">
						<PrimaryActionButton
							:disabled="!userCanUseSiteScanner"
							@click="downloadSingularCookie(cookieToManage.cookieName)"
						>
							Download Cookie
						</PrimaryActionButton>
					</v-col>
				</v-row>
			</div>
		</template>
		<template #modal-footer>
			<SecondaryActionButton @click="closeModal">
				Close
			</SecondaryActionButton>
			<PrimaryActionButton
				v-if="userCanUseSiteScanner"
				@click="updateSelectedCookieDetails()"
			>
				{{ 'Save Changes' }}
			</PrimaryActionButton>
		</template>
	</Modal>
</template>

<script>
import Modal from '../../../../../../../shared/components/modal.vue'
import TextArea from '../../../../../../../shared/components/textarea.vue'
import IconButton from '../../../../../../../shared/components/icon-button.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../../shared/components/secondary-action-button.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import downloadCSVMixin from '../../../../../../../shared/mixins/download-csv.js'
import { downloadScanFile, updateCookieDetails } from '../../../../../../../shared/utils/api/site-scanner.js'
import { downloadTypeEnum } from '../download-type-enums.js'

export default {
	components: {
		Modal,
		TextArea,
		IconButton,
		PrimaryActionButton,
		SecondaryActionButton,
		Dropdown
	},
	mixins: [downloadCSVMixin],
	props: {
		selectedCookie: Object,
		userCanUseSiteScanner: Boolean,
		scanGuid: String,
		cookieCategories: Array,
		totalUrls: Number
	},
	data () {
		return {
			cookieToManage: JSON.parse(JSON.stringify(this.selectedCookie)),
			description: ''
		}
	},
	computed: {
		cookieCategoryItems () {
			return this.cookieCategories.map(category => ({
				value: category.categoryId,
				text: category.categoryName
			}))
		}
	},
	created () {
		this.setDescription()
	},
	methods: {
		closeModal () {
			this.$emit('close')
		},
		restoreDefaultDescription () {
			this.description = this.cookieToManage.defaultCookieDescription
		},
		getPercentage (count) {
			return (count / this.totalUrls) * 100
		},
		cookieUrlCount (count) {
			return count
		},
		async downloadSingularCookie (cookieName) {
			const response = await downloadScanFile(this.scanGuid, downloadTypeEnum.COOKIE_REPORT, cookieName)
			const fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0]
			this.generateCsv(response.data, `${cookieName} - ${fileName}`)
		},
		async updateSelectedCookieDetails () {
			const updateCookieDetailsRequest = {
				ScanGuid: this.scanGuid,
				CookieName: this.cookieToManage.cookieName,
				CookieCategory: this.cookieToManage.categoryId,
				AlternativeDescription: this.description
			}
			await updateCookieDetails(updateCookieDetailsRequest)
			const categoryName = this.cookieCategories.find(category => category.categoryId === this.cookieToManage.categoryId)?.categoryName
			const managedCookie = {
				...this.cookieToManage,
				description: this.description || null,
				category: categoryName
			}
			this.$emit('update-cookie-details', managedCookie)
			this.closeModal()
		},
		setDescription () {
			this.description = this.selectedCookie.description ?? this.selectedCookie.defaultCookieDescription
		}
	}
}
</script>
